import { useState, useCallback, useEffect, useMemo } from 'react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useAdminStore } from '../../stores/adminStore.js'
import { StatCard, Select, Input, Button, Table, Pagination, Alert, Tooltip, type TableColumn } from '../../components/ui/index.js'
import { StatusBadge } from '../../components/admin/index.js'
import { formatDate, formatNumber, formatPercent } from '../../lib/format.js'
import styles from './AdminAIThemes.module.css'

interface AITheme {
  id: string
  prompt: string | null
  modelId: string | null
  modelName: string
  status: string
  failReason: string | null
  inputTokens: number | null
  outputTokens: number | null
  cost: number | null
  durationMs: number | null
  creditsUsed: number | null
  createdAt: string
  userEmail: string
  userName: string | null
}

interface AIThemesResponse {
  themes: AITheme[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: { totalCount: number; totalCost: number; failRate: number; avgDurationMs: number }
}

export function AdminAIThemes() {
  const { filters, setFilters, resetFilters } = useAdminStore()
  const [statusFilter, setStatusFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    if (statusFilter) p.set('status', statusFilter)
    if (searchDebounced) p.set('search', searchDebounced)
    return p.toString()
  }, [filters, statusFilter, searchDebounced])

  const { data, isLoading, error } = useApiFetch<AIThemesResponse>(
    `/api/admin/ai-themes?${params}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const handleResetFilters = useCallback(() => {
    setStatusFilter('')
    setSearchInput('')
    setSearchDebounced('')
    resetFilters()
  }, [resetFilters])

  const columns: TableColumn<AITheme>[] = [
    { key: 'id', label: 'ID', render: (row) => row.id.slice(0, 8), width: '80px' },
    { key: 'userName', label: '유저', render: (row) => row.userName ?? row.userEmail },
    {
      key: 'prompt',
      label: '프롬프트',
      render: (row) => row.prompt ? (
        <Tooltip content={row.prompt}>
          <span className={styles.promptCell}>{row.prompt.slice(0, 40)}{row.prompt.length > 40 ? '...' : ''}</span>
        </Tooltip>
      ) : '-',
    },
    { key: 'modelName', label: '모델', width: '120px' },
    { key: 'status', label: '상태', render: (row) => <StatusBadge status={row.status} />, width: '110px' },
    {
      key: 'failReason',
      label: '실패 사유',
      render: (row) => row.failReason ? (
        <Tooltip content={row.failReason}>
          <span className={styles.promptCell}>{row.failReason.slice(0, 20)}...</span>
        </Tooltip>
      ) : '-',
      width: '120px',
    },
    { key: 'tokens', label: '토큰', render: (row) => row.inputTokens != null ? `${row.inputTokens}/${row.outputTokens ?? 0}` : '-', width: '90px' },
    { key: 'cost', label: '비용($)', sortable: true, render: (row) => row.cost != null ? `$${row.cost.toFixed(4)}` : '-', align: 'right' as const, width: '80px' },
    { key: 'durationMs', label: '소요(ms)', sortable: true, render: (row) => row.durationMs != null ? formatNumber(row.durationMs) : '-', align: 'right' as const, width: '90px' },
    { key: 'createdAt', label: '일시', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 테마 수" value={formatNumber(data?.stats.totalCount ?? 0)} />
        <StatCard label="총 비용" value={`$${(data?.stats.totalCost ?? 0).toFixed(2)}`} />
        <StatCard label="실패율" value={formatPercent(data?.stats.failRate ?? 0)} />
        <StatCard label="평균 소요" value={`${formatNumber(data?.stats.avgDurationMs ?? 0)}ms`} />
      </div>

      <div className={styles.filters}>
        <Input
          size="sm"
          placeholder="유저 이메일/이름 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 상태' },
            { value: 'completed', label: 'completed' },
            { value: 'safelist_failed', label: 'safelist_failed' },
            { value: 'failed', label: 'failed' },
          ]}
        />
        <Button size="sm" variant="ghost" onClick={handleResetFilters}>
          필터 초기화
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AITheme>
        columns={columns}
        data={data?.themes ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
